import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  PackageCheck,
  Plus,
  Truck,
  Package,
  Calendar,
  FileText,
  Check,
  X,
  Barcode
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { motion } from "framer-motion";

const DeliveryPage = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Form state
  const [selectedOrder, setSelectedOrder] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(new Date().toISOString().split('T')[0]);
  const [deliveryNote, setDeliveryNote] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [deliveriesRes, ordersRes, productsRes] = await Promise.all([
        axios.get(`${API}/deliveries`),
        axios.get(`${API}/orders?status=ordered`),
        axios.get(`${API}/products`)
      ]);
      setDeliveries(deliveriesRes.data);
      // Also include partial orders
      const partialOrders = await axios.get(`${API}/orders?status=partial`);
      setOrders([...ordersRes.data, ...partialOrders.data]);
      setProducts(productsRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const openNewDelivery = () => {
    setSelectedOrder("");
    setDeliveryDate(new Date().toISOString().split('T')[0]);
    setDeliveryNote("");
    setItems([]);
    setDialogOpen(true);
  };

  const loadOrderItems = async (orderId) => {
    if (!orderId) {
      setItems([]);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/orders/${orderId}`);
      const orderItems = res.data.items.map(item => ({
        order_item_id: item.id,
        product_id: item.product_id,
        product_name: item.product_name,
        quantity_ordered: item.quantity_ordered,
        quantity_received: item.quantity_received || 0,
        quantity: Math.max(0, item.quantity_ordered - (item.quantity_received || 0)),
        expiry_date: ""
      }));
      setItems(orderItems);
    } catch (err) {
      toast.error("Fehler beim Laden der Bestellung");
    }
  };

  const handleOrderChange = (orderId) => {
    setSelectedOrder(orderId);
    loadOrderItems(orderId);
  };

  const addManualItem = () => {
    setItems([...items, {
      product_id: "",
      product_name: "",
      quantity: 1,
      expiry_date: ""
    }]);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // Update product name when product changes
    if (field === "product_id") {
      const product = products.find(p => p.id === value);
      newItems[index].product_name = product?.name || "";
    }
    
    setItems(newItems);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (items.length === 0) {
      toast.error("Bitte mindestens eine Position hinzufügen");
      return;
    }

    const validItems = items.filter(item => item.product_id && item.quantity > 0);
    if (validItems.length === 0) {
      toast.error("Bitte gültige Positionen eingeben");
      return;
    }

    try {
      await axios.post(`${API}/deliveries`, {
        order_id: selectedOrder || null,
        delivery_date: deliveryDate,
        delivery_note_number: deliveryNote,
        items: validItems.map(item => ({
          product_id: item.product_id,
          quantity: parseInt(item.quantity),
          expiry_date: item.expiry_date || null,
          order_item_id: item.order_item_id || null
        }))
      });

      toast.success("Wareneingang erfolgreich gebucht");
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Buchen");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("de-DE");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="delivery-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Wareneingang
          </h1>
          <p className="text-slate-500 mt-1">
            Lieferungen erfassen und ins Lager buchen
          </p>
        </div>
        <Button
          onClick={openNewDelivery}
          className="bg-rose-600 hover:bg-rose-700"
          data-testid="new-delivery-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Wareneingang buchen
        </Button>
      </div>

      {/* Open Orders Info */}
      {orders.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Truck className="w-6 h-6 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">
                  {orders.length} offene Bestellung{orders.length !== 1 ? "en" : ""}
                </p>
                <p className="text-sm text-blue-700">
                  Warten auf Lieferung oder sind teilweise geliefert
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deliveries List */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PackageCheck className="w-5 h-5" />
            Letzte Wareneingänge
          </CardTitle>
        </CardHeader>
        <CardContent>
          {deliveries.length === 0 ? (
            <p className="text-center text-slate-500 py-8">
              Noch keine Wareneingänge erfasst
            </p>
          ) : (
            <div className="space-y-3">
              {deliveries.map((delivery) => (
                <motion.div
                  key={delivery.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <PackageCheck className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {delivery.supplier_name}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Calendar className="w-3 h-3" />
                        {formatDate(delivery.delivery_date)}
                        {delivery.delivery_note_number && (
                          <>
                            <span>•</span>
                            <FileText className="w-3 h-3" />
                            {delivery.delivery_note_number}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <Badge variant="outline">
                    {delivery.items_count} Position{delivery.items_count !== 1 ? "en" : ""}
                  </Badge>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Delivery Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageCheck className="w-5 h-5" />
              Wareneingang buchen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Order Selection */}
            <div className="space-y-2">
              <Label>Zu Bestellung zuordnen (optional)</Label>
              <Select value={selectedOrder} onValueChange={handleOrderChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Ohne Bestellung / Direktkauf" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Ohne Bestellung</SelectItem>
                  {orders.map((order) => (
                    <SelectItem key={order.id} value={order.id}>
                      {order.supplier_name} - {order.order_number || formatDate(order.order_date)}
                      {order.status === "partial" && " (Teillieferung)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Delivery Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lieferdatum</Label>
                <Input
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Lieferschein-Nr. (optional)</Label>
                <Input
                  value={deliveryNote}
                  onChange={(e) => setDeliveryNote(e.target.value)}
                  placeholder="z.B. LS-12345"
                />
              </div>
            </div>

            {/* Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Positionen</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addManualItem}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Position hinzufügen
                </Button>
              </div>

              {items.length === 0 ? (
                <p className="text-center text-slate-500 py-4 border rounded-lg border-dashed">
                  {selectedOrder 
                    ? "Bestellung laden..."
                    : "Keine Positionen. Fügen Sie Produkte hinzu."}
                </p>
              ) : (
                <div className="space-y-2">
                  {items.map((item, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-12 gap-2 items-end p-3 border rounded-lg bg-slate-50"
                    >
                      {/* Product */}
                      <div className="col-span-4 space-y-1">
                        <Label className="text-xs">Produkt</Label>
                        {item.order_item_id ? (
                          <p className="text-sm font-medium py-2">{item.product_name}</p>
                        ) : (
                          <Select
                            value={item.product_id}
                            onValueChange={(v) => updateItem(index, "product_id", v)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Wählen..." />
                            </SelectTrigger>
                            <SelectContent>
                              {products.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>

                      {/* Quantity Info (if from order) */}
                      {item.order_item_id && (
                        <div className="col-span-2 space-y-1">
                          <Label className="text-xs">Bestellt/Geliefert</Label>
                          <p className="text-sm py-2">
                            {item.quantity_received}/{item.quantity_ordered}
                          </p>
                        </div>
                      )}

                      {/* Quantity */}
                      <div className={`${item.order_item_id ? 'col-span-2' : 'col-span-3'} space-y-1`}>
                        <Label className="text-xs">Menge</Label>
                        <Input
                          type="number"
                          min="0"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, "quantity", e.target.value)}
                          className="h-9"
                        />
                      </div>

                      {/* Expiry Date */}
                      <div className="col-span-3 space-y-1">
                        <Label className="text-xs">MHD</Label>
                        <Input
                          type="date"
                          value={item.expiry_date}
                          onChange={(e) => updateItem(index, "expiry_date", e.target.value)}
                          className="h-9"
                        />
                      </div>

                      {/* Remove */}
                      <div className="col-span-1 flex justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 text-slate-400 hover:text-red-600"
                          onClick={() => removeItem(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button
                onClick={handleSubmit}
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={items.length === 0}
              >
                <Check className="w-4 h-4 mr-2" />
                Wareneingang buchen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryPage;
